import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import { DropdownModule, IconModule } from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';

import { GetSecureCardDetailsRes } from '@iw/cards';
import { Account, AccountType } from '@iw/core/entities';

import { CardViewAction, CardViewSize } from './card-view.model';
import { ButtonComponent } from '../button';
import { BankCardComponent } from './bank-card';
import { BankCardBackComponent } from './bank-card-back';
import { CryptoWalletComponent } from './crypto-wallet';
import { FiatWalletComponent } from './fiat-wallet';
import { TreasurerWalletComponent } from './treasurer-wallet';

@Component({
  imports: [
    NgClass,
    ButtonComponent,
    IconModule,
    DropdownModule,
    TranslateModule,
    BankCardComponent,
    BankCardBackComponent,
    FiatWalletComponent,
    CryptoWalletComponent,
    TreasurerWalletComponent,
  ],
  selector: 'iw-card-view, [iwCardView]',
  templateUrl: './card-view.component.html',
  styleUrls: ['./card-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('flipState', [
      state(
        'active',
        style({
          transform: 'rotateY(179deg)',
        }),
      ),
      state(
        'inactive',
        style({
          transform: 'rotateY(0)',
        }),
      ),
      transition('active => inactive', animate('500ms ease-out')),
      transition('inactive => active', animate('500ms ease-in')),
    ]),
  ],
})
export class CardViewComponent {
  readonly account = input.required<Account>();
  readonly accountType = input<AccountType>(AccountType.FiatWallet);
  readonly size = input<CardViewSize>();
  readonly actions = input<CardViewAction[]>();
  readonly defaultFlipState = input<'inactive' | 'active'>('inactive');
  readonly flipMode = input<boolean>(false);
  readonly isLoading = input<boolean>(false);
  readonly sensitiveData = input<GetSecureCardDetailsRes | null>(null);
  readonly isSensitiveDataHidden = model<boolean>(true);
  readonly useBalanceMask = input<boolean>(false);

  readonly action = output<Account>();

  readonly AccountType = AccountType;
  readonly iconSize: Record<CardViewSize, number> = {
    mobile: 24,
    xxs: 20,
    xs: 16,
    s: 24,
    m: 24,
    l: 24,
    xl: 24,
  };

  readonly opened = signal(false);
  readonly flipState = signal<'inactive' | 'active'>('inactive');

  readonly currentCard = computed(() => this.account());
  readonly currentSize = computed(() => this.size() ?? 'l');

  readonly cardImageUrl = computed(() => {
    const accountType = this.accountType();
    return `/assets/images/cards/${accountType}-${this.currentSize()}.png`;
  });

  readonly flipEnabled = computed(() => {
    return this.flipMode() || this.defaultFlipState() === 'active';
  });

  readonly isMenuHidden = computed(() => {
    const hasActions = !!this.actions()?.length;
    const notMedium = this.size() !== 'm';

    return !(notMedium && hasActions);
  });

  readonly viewClasses = computed(() => {
    const type = this.accountType();
    const size = this.size() ?? 'm';

    return {
      flip: this.flipEnabled(),
      [type]: true,
      [`card-view-${size}`]: true,
    };
  });

  defaultFlipSetEffect = effect(() => {
    this.flipState.set(this.defaultFlipState());
  });

  showOverlay(): void {
    this.opened.set(true);
  }

  toggleFlip() {
    if (!this.flipMode()) return;

    this.flipState.update((prev) =>
      prev === 'inactive' ? 'active' : 'inactive',
    );
  }
}
